import React, { useMemo, useRef, useState } from "react";

import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Stack } from "@mui/system";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import { formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import useActionMarineTraffic from "../../../../hooks/useActionMarineTraffic";
import { ContainerStatus, Event, EventStatus, Location } from "../../../../models/marine.traffic";
import { IDashboardData, IDashboardMapData } from "../../../../models/overview/dashboard";
import ShipmentViewComponent, {
  ShipmentViewHandles,
} from "../../../../pages/private/SupplyChainPlus/Shipments/View/index";
import { BookingTabs } from "../../../Booking/util";
import NotEnoughData from "../../../DashMetrics/NotEnoughData";
import { ProgressBarContainerEvent } from "../../../Tracking";
import { CodeLabel, DateLabel, useStyles } from "./styles";

const CENTER_MAP = { lat: 0, lng: 0 };
const MARKER_ICON = require("Assets/img/overview-config/track-marker.svg");

const ZOOM_MAP = 3;

type InfoWindowContentProps = {
  info: IDashboardMapData;
  onClose: () => void;
  onOpenShipmentModal: (shipmentId: number) => void;
};

const InfoWindowContent = ({ info, onClose, onOpenShipmentModal }: InfoWindowContentProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { getContainerStatus } = useActionMarineTraffic();

  const [containerStatus, setContainerStatus] = useState<ContainerStatus>();
  const { isFetching: isFetchingStatus } = getContainerStatus(
    setContainerStatus,
    info.marineTrafficId ?? ""
  );

  const statusProgressBar = useMemo(() => {
    if (
      !containerStatus ||
      !containerStatus.transportationPlan ||
      !containerStatus.transportationPlan.events
    )
      return;

    const { routes, events, locations } = containerStatus.transportationPlan;

    let transportLegs = containerStatus.transportationPlan?.transportLegs;

    let departureEvent: Event | undefined;
    let loadEvent = events.find((event: Event) => event.eventTypeCode === "CGI");

    if (routes && !routes.length) {
      const originLocationPortId = locations.find(
        (location) => location.locationName === containerStatus.originPortName
      )?.portId;

      const filteredEvents = events.filter(
        (event) => event.location.portId === originLocationPortId
      );

      departureEvent = filteredEvents?.[filteredEvents.length - 1];
      loadEvent = {
        ...loadEvent,
        eventDatetime: containerStatus.originPortActualDepartureUtc,
        eventStatus: "ACT",
      } as unknown as Event;
    } else {
      const departureEventId: number = routes[0]?.departure.eventId || 0;

      departureEvent = events.find((event: Event) => event.eventId === departureEventId);
    }

    const departureLocation = locations.find(
      (location: Location) => location.portId === departureEvent?.location.portId
    );

    let transportLegsEvents: ProgressBarContainerEvent[] = [];
    const date = loadEvent?.eventDatetime ? new Date(loadEvent?.eventDatetime) : null;

    transportLegsEvents.push({
      eventTypeDescription: translate("containerGateInPort"),
      eventLocationName: departureLocation?.locationName ?? "",
      nextEventDateTime: date ? `${formatInTimeZone(date, "UTC", "dd/MM/yyyy hh:mm aa")} UTC` : "",
      iconName: "home",
      status:
        loadEvent?.eventDatetime && loadEvent?.eventStatus === "ACT"
          ? EventStatus.DONE
          : EventStatus.WAITING,
      portId: departureLocation?.portId,
    });

    if (transportLegs && !transportLegs.length) {
      const arrivalLocation = locations.find(
        (location) => location.locationName === containerStatus.finalPortName
      );

      transportLegs = [
        {
          transportLegId: "",
          departureLocation: { portId: departureEvent?.location.portId ?? 0 },
          arrivalLocation: { portId: arrivalLocation?.portId ?? 0 },
        },
      ];
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < transportLegs.length; i++) {
      const transportLeg = transportLegs[i];

      const arrivalPortId = transportLeg.arrivalLocation.portId;
      const departurePortId = transportLeg.departureLocation.portId;

      const arrivalLocation = locations.find((location) => location.portId === arrivalPortId);
      const departureLocation = locations.find((location) => location.portId === departurePortId);

      // TODO: This removed duplicated ports. Should be deleted?
      // const transportLegsEventsAlreadyHasPortId = transportLegsEvents.some(
      //   (transport) => transport.portId === arrivalLocation?.portId
      // );

      // if (transportLegsEventsAlreadyHasPortId) continue;

      if (arrivalLocation) {
        const oldStatusEvent = transportLegsEvents[i]?.status;

        // eslint-disable-next-line no-nested-ternary
        const date = transportLeg.portActualArrivalUtc
          ? new Date(transportLeg.portActualArrivalUtc)
          : transportLeg.portPredictiveArrivalUtc
          ? new Date(transportLeg.portPredictiveArrivalUtc)
          : null;

        // eslint-disable-next-line no-nested-ternary
        const newStatus = transportLeg.portActualArrivalUtc
          ? EventStatus.DONE
          : oldStatusEvent === EventStatus.DONE
          ? EventStatus.IN_PROGRESS
          : EventStatus.WAITING;

        const verifyDate =
          containerStatus.finalPortActualArrivalUtc ||
          containerStatus.finalPortPredictiveArrivalUtc ||
          containerStatus.finalPortPredictiveArrivalUtcLast ||
          null;

        const locationIsThatLastPort =
          arrivalLocation.locationName === containerStatus.finalPortName;

        if (
          !date ||
          (date && verifyDate && date.getTime() <= new Date(verifyDate).getTime()) ||
          locationIsThatLastPort
        ) {
          transportLegsEvents.push({
            eventTypeDescription: `${
              // eslint-disable-next-line no-nested-ternary
              newStatus !== EventStatus.DONE
                ? date
                  ? translate("predictiveArrivalAt")
                  : translate("waitingForUpdate")
                : translate("arrivalAt")
            }`,
            eventLocationName: arrivalLocation?.locationName ?? "",
            nextEventDateTime: date
              ? `${formatInTimeZone(date, "UTC", "dd/MM/yyyy hh:mm aa")} UTC`
              : "",
            iconName: "ship",
            status: newStatus,
            portId: arrivalLocation?.portId,
          });
        }
      }

      if (departureLocation && transportLeg.portActualDepartureUtc) {
        const transport = transportLegsEvents.find(
          (legs) => legs.eventLocationName === departureLocation.locationName
        );
        const oldStatusEvent = transport?.status;
        const eventDateTime = events.find(
          (event) => event.location.portId === departurePortId
        )?.eventDatetime;

        if (oldStatusEvent !== EventStatus.DONE) {
          transportLegsEvents[i] = {
            ...transportLegsEvents[i],
            status: EventStatus.DONE,
            // eslint-disable-next-line no-nested-ternary
            nextEventDateTime: transportLegsEvents[i].nextEventDateTime
              ? transportLegsEvents[i].nextEventDateTime
              : eventDateTime
              ? `${formatInTimeZone(new Date(eventDateTime), "UTC", "dd/MM/yyyy hh:mm aa")} UTC`
              : "",
          };
        }
      }
    }

    const activeStep = transportLegsEvents.findIndex(
      (transportLegEvent: ProgressBarContainerEvent) =>
        transportLegEvent.status === EventStatus.IN_PROGRESS
    );

    if (activeStep && activeStep !== -1) {
      transportLegsEvents
        .slice(activeStep + 1, transportLegsEvents.length)
        .forEach((event: ProgressBarContainerEvent) => {
          event.status = EventStatus.WAITING;
        });
    }
    transportLegsEvents = transportLegsEvents.map(
      (transportLegEvent: ProgressBarContainerEvent, index) => ({
        ...transportLegEvent,
        events: !(transportLegsEvents.length - 1 === index)
          ? events
              .filter((event) => event.location.portId === transportLegEvent?.portId)
              .map((event) => ({
                ...event,
                vessel: {
                  ...event.vessel,
                  vesselName: containerStatus?.transportationPlan.vessels.find(
                    (vessel) => vessel.shipId === event.vessel.shipId
                  )?.vesselName,
                },
              }))
          : [],
      })
    );

    return {
      events: transportLegsEvents,
      activeStep: activeStep || -1,
    };
  }, [containerStatus]);

  const steps = useMemo(() => {
    return statusProgressBar?.events || [];
  }, [statusProgressBar]);

  const locations = containerStatus?.transportationPlan?.locations;

  const firstStep = useMemo(() => steps[0], [steps]);
  const lastStep = useMemo(() => steps?.[Number(steps.length - 1) ?? 0], [steps]);
  const firstStepPort = useMemo(
    () => locations?.find((location) => location.portId === firstStep.portId),
    [firstStep, locations]
  );
  const lastStepPort = useMemo(
    () => locations?.find((location) => location.portId === lastStep.portId),
    [lastStep, locations]
  );

  const renderLocation = (location: Location | undefined) => {
    if (!location) return null;

    const { UNLocationCode: code } = location;
    const countryCode = code?.slice(0, 2);
    const cityCode = code?.slice(2, code?.length);

    return (
      <div>
        <CodeLabel active isCountry>
          {countryCode}
        </CodeLabel>
        <CodeLabel active>{cityCode}</CodeLabel>
      </div>
    );
  };

  const renderLocations = () => {
    return (
      <div className={classes.portsContainer}>
        <div className={classes.portsItems}>
          <span>{`${translate("from")}: `}</span>
          {renderLocation(firstStepPort)}
        </div>
        <div className={classes.portsItems}>
          <span>{`${translate("to")}: `}</span>
          {renderLocation(lastStepPort)}
        </div>
      </div>
    );
  };

  const renderLoading = () => {
    return (
      <Box
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        className={classes.loading}
      >
        <CircularProgress size={30}></CircularProgress>
      </Box>
    );
  };

  const renderCardContent = () => {
    return (
      <>
        <CardHeader
          action={
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          }
          className={classes.cardHeader}
          subheader={
            <span onClick={() => onOpenShipmentModal(info?.id || 0)}>{`${translate("shipment")} #${
              info.containerId
            }`}</span>
          }
          title={
            <span onClick={() => onOpenShipmentModal(info?.id || 0)}>{`${translate("vessel")} ${
              containerStatus?.vesselName || ""
            }`}</span>
          }
        />
        <div className={classes.separator}></div>
        <CardContent className={classes.cardContent}>
          <Stack direction="column" spacing={1} sx={{ width: "100%" }}>
            <div>{renderLocations()}</div>
            {info.atd && (
              <div className={classes.atdMobile}>
                <DateLabel>{translate("atd")}: </DateLabel>
                <DateLabel light>
                  {info.atd
                    ? `${formatInTimeZone(info.atd, "UTC", "dd/MM/yyyy \u25CF hh:mm aa")} UTC`
                    : "\u00A0"}
                </DateLabel>
              </div>
            )}
            {info.eta && (
              <div className={classes.atdMobile}>
                <DateLabel>{translate("eta")}: </DateLabel>
                <div>
                  <DateLabel light>
                    {info.eta
                      ? `${formatInTimeZone(info.eta, "UTC", "dd/MM/yyyy \u25CF hh:mm aa")} UTC`
                      : "\u00A0"}
                  </DateLabel>
                </div>
              </div>
            )}
          </Stack>
        </CardContent>
        <CardActions className={classes.cardActions}>
          <div>{info?.status ? translate(info?.status) : ""}</div>
        </CardActions>
      </>
    );
  };

  return (
    <Card className={classes.card}>{isFetchingStatus ? renderLoading() : renderCardContent()}</Card>
  );
};

interface Props {
  datasets: IDashboardData[];
}

const OverviewBubbleMapGraph = ({ datasets }: Props) => {
  const classes = useStyles();
  const sideViewRef = useRef<ShipmentViewHandles>(null);

  const handleOpenShipmentView = (shipmentId: number) => {
    sideViewRef.current?.open(Number(shipmentId));
  };

  if (!datasets[0].data.length) return <NotEnoughData />;
  const infos: IDashboardMapData[] = datasets[0].data as IDashboardMapData[];
  const [showInfoWindow, setShowInfoWindow] = useState<boolean[]>(
    new Array(infos.length).fill(false)
  );

  const handleMarkerClick = (index: number, value: boolean) => {
    const showInfo = [...showInfoWindow];

    showInfo[index] = value;
    setShowInfoWindow(showInfo);
  };

  const center = useMemo(() => {
    const { length } = infos;

    if (!length) return CENTER_MAP;

    const { lat, lng } = infos.reduce(
      (prev, curr) => ({
        lat: prev.lat + (curr.latitude || 0),
        lng: prev.lng + (curr.longitude || 0),
      }),
      { lat: 0, lng: 0 }
    );

    return { lat: lat / length, lng: lng / length };
  }, [infos]);

  return (
    <>
      <GoogleMap center={center} mapContainerClassName={classes.map} zoom={ZOOM_MAP}>
        {infos.map((info: IDashboardMapData, index: number) => (
          <>
            {showInfoWindow[index] && (
              <OverlayView
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                position={{ lat: info.latitude || 0, lng: info.longitude || 0 }}
              >
                <InfoWindowContent
                  info={info}
                  onClose={() => handleMarkerClick(index, false)}
                  onOpenShipmentModal={handleOpenShipmentView}
                />
              </OverlayView>
            )}
            <Marker
              icon={{
                url: MARKER_ICON,
                anchor: new google.maps.Point(1, 5),
                scaledSize: new google.maps.Size(20, 20),
              }}
              key={info.label || `marker_${index}`}
              position={{ lat: info.latitude || 0, lng: info.longitude || 0 }}
              onClick={() => handleMarkerClick(index, true)}
            ></Marker>
          </>
        ))}
      </GoogleMap>
      <ShipmentViewComponent ref={sideViewRef} defaultTab={BookingTabs.TRACKING} />
    </>
  );
};

export default OverviewBubbleMapGraph;
